import { create } from "zustand";
import type { Board, Card, Lane, ChatMessage, SortKey, DeadlinePreset, Mood, Category, DailyOverview, UserSettings } from "../types";
import { api } from "../lib/api";

const FILTERS_KEY = "onboard.filters.v1";
const CHAT_OPEN_KEY = "onboard.chat.open";
const BANNER_DISMISSED_KEY = "onboard.daily.dismissed";
const ACTIVE_BOARD_KEY = "onboard.activeBoardId";
const SIDEBAR_COLLAPSED_KEY = "onboard.sidebarCollapsed";

function readActiveBoardFromUrl(): number | null {
  if (typeof window === "undefined") return null;
  try {
    const v = new URL(window.location.href).searchParams.get("board");
    if (!v) return null;
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function readActiveBoardFromStorage(): number | null {
  if (typeof window === "undefined") return null;
  try {
    const v = localStorage.getItem(ACTIVE_BOARD_KEY);
    if (!v) return null;
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function persistActiveBoard(id: number | null, isPrimary: boolean): void {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    if (id == null || isPrimary) {
      url.searchParams.delete("board");
    } else {
      url.searchParams.set("board", String(id));
    }
    history.replaceState(null, "", url.toString());
    if (id == null) localStorage.removeItem(ACTIVE_BOARD_KEY);
    else localStorage.setItem(ACTIVE_BOARD_KEY, String(id));
  } catch {}
}

function readSidebarCollapsed(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

interface PersistedFilters {
  search: string;
  priority: string | null;
  categoryId: number | null;
  mood: Mood | null;
  deadlinePreset: DeadlinePreset | null;
  sort: SortKey;
}

function readPersisted(): PersistedFilters {
  const defaults: PersistedFilters = {
    search: "",
    priority: null,
    categoryId: null,
    mood: null,
    deadlinePreset: null,
    sort: "manual",
  };
  try {
    const url = new URL(window.location.href);
    const fromUrl = url.searchParams;
    const hasAny = ["q", "priority", "cat", "mood", "due", "sort"].some((k) => fromUrl.has(k));
    if (hasAny) {
      const catParam = fromUrl.get("cat");
      return {
        search: fromUrl.get("q") || "",
        priority: fromUrl.get("priority"),
        categoryId: catParam ? Number(catParam) : null,
        mood: (fromUrl.get("mood") as Mood | null) || null,
        deadlinePreset: (fromUrl.get("due") as DeadlinePreset | null) || null,
        sort: (fromUrl.get("sort") as SortKey) || "manual",
      };
    }
    const stored = localStorage.getItem(FILTERS_KEY);
    if (stored) return { ...defaults, ...JSON.parse(stored) };
  } catch {}
  return defaults;
}

function persistFilters(f: PersistedFilters): void {
  try {
    localStorage.setItem(FILTERS_KEY, JSON.stringify(f));
    const url = new URL(window.location.href);
    const sp = url.searchParams;
    const sync = (k: string, v: string | null | undefined) => {
      if (v) sp.set(k, v);
      else sp.delete(k);
    };
    sync("q", f.search);
    sync("priority", f.priority);
    sync("cat", f.categoryId != null ? String(f.categoryId) : null);
    sync("mood", f.mood);
    sync("due", f.deadlinePreset);
    sync("sort", f.sort === "manual" ? null : f.sort);
    history.replaceState(null, "", url.toString());
  } catch {}
}

interface State {
  boards: Board[];
  activeBoardId: number | null;
  sidebarCollapsed: boolean;
  lanes: Lane[];
  cards: Card[];
  categories: Category[];
  loading: boolean;

  userSettings: UserSettings;
  onboardingOpen: boolean;
  userEmail: string | null;

  searchQuery: string;
  priorityFilter: string | null;
  categoryFilter: number | null;
  moodFilter: Mood | null;
  deadlineFilter: DeadlinePreset | null;
  sort: SortKey;
  focusLaneId: number | null;

  activeCardId: number | null;
  /** ID do card recém-criado pelo IAGO; CardView aplica halo gradiente. Auto-limpa. */
  freshCardId: number | null;
  tourOpen: boolean;
  settingsOpen: boolean;
  health: any;

  chatOpen: boolean;
  chatMessages: ChatMessage[];
  chatSending: boolean;

  daily: DailyOverview | null;
  dailyDismissed: boolean;
  dailyLoading: boolean;

  load: () => Promise<void>;
  loadBoardContent: (boardId: number) => Promise<void>;
  switchBoard: (id: number) => Promise<void>;
  createBoard: (title: string, opts?: { icon?: string | null; color?: string | null }) => Promise<Board>;
  renameBoard: (id: number, title: string) => Promise<Board>;
  updateBoardVisuals: (id: number, patch: { icon?: string | null; color?: string | null }) => Promise<Board>;
  archiveBoard: (id: number) => Promise<void>;
  unarchiveBoard: (id: number) => Promise<void>;
  setSidebarCollapsed: (v: boolean) => void;
  setActiveCard: (id: number | null) => void;
  setSearch: (q: string) => void;
  setPriorityFilter: (p: string | null) => void;
  setCategoryFilter: (id: number | null) => void;
  setMoodFilter: (m: Mood | null) => void;
  setDeadlineFilter: (d: DeadlinePreset | null) => void;
  setSort: (s: SortKey) => void;
  setFocusLane: (id: number | null) => void;
  setTourOpen: (v: boolean) => void;
  setSettingsOpen: (v: boolean) => void;
  clearFilters: () => void;

  moveCardLocal: (cardId: number, targetLaneId: number, targetIndex: number) => void;
  upsertCard: (card: Card) => void;
  removeCard: (id: number) => void;
  /** Marca card como "recém-criado pelo IAGO"; auto-limpa após 1500ms. */
  markFreshCard: (id: number) => void;
  upsertLane: (lane: Lane) => void;
  removeLane: (id: number) => void;

  createLane: (title: string, color?: string) => Promise<void>;
  updateCard: (id: number, patch: Partial<Card>) => Promise<Card>;
  deleteCard: (id: number) => Promise<void>;
  duplicateCard: (id: number) => Promise<Card>;
  createCategory: (body: { name: string; color?: string; kind?: string }) => Promise<Category>;
  updateCategoryItem: (id: number, patch: Partial<Category>) => Promise<void>;
  deleteCategoryItem: (id: number) => Promise<void>;

  loadDaily: () => Promise<void>;
  dismissDaily: () => void;

  loadSettings: () => Promise<void>;
  saveGroqKey: (key: string) => Promise<void>;
  removeGroqKey: () => Promise<void>;
  saveNickname: (name: string) => Promise<void>;
  saveWeightLaneIds: (ids: number[]) => Promise<void>;
  markTutorialCompleted: () => Promise<void>;
  dismissIagoBalloon: () => Promise<void>;
  setOnboardingOpen: (v: boolean) => void;
  setUserEmail: (email: string | null) => void;
  resetOnSignOut: () => void;

  toggleChat: () => void;
  loadChat: () => Promise<void>;
  sendChat: (content: string) => Promise<void>;
  approveAction: (messageId: number, index: number) => Promise<void>;
  clearChat: () => Promise<void>;
}

const persisted = typeof window !== "undefined" ? readPersisted() : {
  search: "", priority: null, categoryId: null, mood: null, deadlinePreset: null, sort: "manual" as SortKey,
};
const chatOpenInit = typeof window !== "undefined" && localStorage.getItem(CHAT_OPEN_KEY) === "1";

function isDailyDismissedToday(): boolean {
  if (typeof window === "undefined") return false;
  const stored = localStorage.getItem(BANNER_DISMISSED_KEY);
  if (!stored) return false;
  return stored === new Date().toISOString().slice(0, 10);
}

export const useStore = create<State>((set, get) => {
  const syncFilters = () => {
    const s = get();
    persistFilters({
      search: s.searchQuery,
      priority: s.priorityFilter,
      categoryId: s.categoryFilter,
      mood: s.moodFilter,
      deadlinePreset: s.deadlineFilter,
      sort: s.sort,
    });
  };

  return {
    boards: [],
    activeBoardId: null,
    sidebarCollapsed: readSidebarCollapsed(),
    lanes: [],
    cards: [],
    categories: [],
    loading: true,
    searchQuery: persisted.search,
    priorityFilter: persisted.priority,
    categoryFilter: persisted.categoryId,
    moodFilter: persisted.mood,
    deadlineFilter: persisted.deadlinePreset,
    sort: persisted.sort,
    focusLaneId: null,
    activeCardId: null,
    freshCardId: null,
    tourOpen: false,
    settingsOpen: false,
    health: null,

    chatOpen: chatOpenInit,
    chatMessages: [],
    chatSending: false,

    daily: null,
    dailyDismissed: isDailyDismissedToday(),
    dailyLoading: false,

    userSettings: { hasGroqKey: false, groqLast4: null, nickname: null, tutorialCompleted: false, iagoBalloonDismissedAt: null, weightLaneIds: [] },
    onboardingOpen: false,
    userEmail: null,

    load: async () => {
      set({ loading: true });
      const [boards, health] = await Promise.all([
        api.getBoards(false),
        api.health().catch(() => null),
      ]);
      const sortedBoards = [...boards].sort((a, b) => {
        if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
        return a.position - b.position;
      });
      const primary = sortedBoards.find((b) => b.isPrimary) ?? sortedBoards[0] ?? null;

      // resolve activeBoardId: URL > localStorage > primary
      const urlId = readActiveBoardFromUrl();
      const storageId = readActiveBoardFromStorage();
      const candidates = [urlId, storageId];
      let chosen: Board | null = null;
      for (const cand of candidates) {
        if (cand == null) continue;
        const match = sortedBoards.find((b) => b.id === cand);
        if (match) { chosen = match; break; }
      }
      if (!chosen) chosen = primary;

      set({ boards: sortedBoards, health });

      if (chosen) {
        set({ activeBoardId: chosen.id });
        persistActiveBoard(chosen.id, chosen.isPrimary);
        await get().loadBoardContent(chosen.id);
      } else {
        set({ lanes: [], cards: [], categories: [], loading: false });
      }

      get().loadDaily();
    },

    loadBoardContent: async (boardId) => {
      set({ loading: true });
      try {
        const content = await api.getBoardContent(boardId);
        set({
          lanes: content.lanes.sort((a, b) => a.position - b.position),
          cards: content.cards.sort((a, b) => a.position - b.position),
          categories: content.categories.sort((a, b) => a.name.localeCompare(b.name)),
          loading: false,
        });
      } catch (e) {
        set({ loading: false });
        throw e;
      }
    },

    switchBoard: async (id) => {
      const board = get().boards.find((b) => b.id === id);
      if (!board) return;
      set({ activeBoardId: id, focusLaneId: null });
      persistActiveBoard(id, board.isPrimary);
      await get().loadBoardContent(id);
    },

    createBoard: async (title, opts) => {
      const result = await api.createBoard({
        title,
        icon: opts?.icon ?? null,
        color: opts?.color ?? null,
      });
      const newBoards = [...get().boards, result.board].sort((a, b) => {
        if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
        return a.position - b.position;
      });
      set({ boards: newBoards });
      await get().switchBoard(result.board.id);
      return result.board;
    },

    renameBoard: async (id, title) => {
      const updated = await api.updateBoard(id, { title });
      set({
        boards: get().boards.map((b) => (b.id === id ? updated : b)),
      });
      return updated;
    },

    updateBoardVisuals: async (id, patch) => {
      const updated = await api.updateBoard(id, patch);
      set({
        boards: get().boards.map((b) => (b.id === id ? updated : b)),
      });
      return updated;
    },

    archiveBoard: async (id) => {
      await api.archiveBoard(id);
      const wasActive = get().activeBoardId === id;
      const remaining = get().boards.map((b) =>
        b.id === id ? { ...b, archived: true, archivedAt: new Date().toISOString() } : b,
      );
      set({ boards: remaining });
      if (wasActive) {
        const primary = remaining.find((b) => b.isPrimary);
        if (primary) await get().switchBoard(primary.id);
      }
    },

    unarchiveBoard: async (id) => {
      const updated = await api.updateBoard(id, { archived: false });
      set({
        boards: get().boards.map((b) => (b.id === id ? updated : b)),
      });
    },

    setSidebarCollapsed: (v) => {
      set({ sidebarCollapsed: v });
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, v ? "1" : "0"); } catch {}
    },

    setActiveCard: (id) => set({ activeCardId: id }),
    setSearch: (q) => { set({ searchQuery: q }); syncFilters(); },
    setPriorityFilter: (p) => { set({ priorityFilter: p }); syncFilters(); },
    setCategoryFilter: (id) => { set({ categoryFilter: id }); syncFilters(); },
    setMoodFilter: (m) => { set({ moodFilter: m }); syncFilters(); },
    setDeadlineFilter: (d) => { set({ deadlineFilter: d }); syncFilters(); },
    setSort: (s) => { set({ sort: s }); syncFilters(); },
    setFocusLane: (id) => set({ focusLaneId: id }),
    setTourOpen: (v) => set({ tourOpen: v }),
    setSettingsOpen: (v) => set({ settingsOpen: v }),
    clearFilters: () => {
      set({
        searchQuery: "", priorityFilter: null, categoryFilter: null,
        moodFilter: null, deadlineFilter: null, sort: "manual",
      });
      syncFilters();
    },

    moveCardLocal: (cardId, targetLaneId, targetIndex) => {
      const { cards } = get();
      const card = cards.find((c) => c.id === cardId);
      if (!card) return;

      const others = cards.filter((c) => c.id !== cardId);
      const targetLaneCards = others
        .filter((c) => c.laneId === targetLaneId)
        .sort((a, b) => a.position - b.position);

      const clampedIndex = Math.max(0, Math.min(targetIndex, targetLaneCards.length));

      if (card.laneId === targetLaneId) {
        const currentList = cards
          .filter((c) => c.laneId === targetLaneId)
          .sort((a, b) => a.position - b.position);
        const currentIdx = currentList.findIndex((c) => c.id === cardId);
        if (currentIdx === clampedIndex) return;
      }

      targetLaneCards.splice(clampedIndex, 0, { ...card, laneId: targetLaneId });

      const rebuilt: Card[] = [];
      for (const c of others) {
        if (c.laneId !== targetLaneId) rebuilt.push(c);
      }
      targetLaneCards.forEach((c, i) => rebuilt.push({ ...c, position: i, laneId: targetLaneId }));
      set({ cards: rebuilt });
    },

    upsertCard: (card) => {
      const { cards } = get();
      const idx = cards.findIndex((c) => c.id === card.id);
      if (idx === -1) set({ cards: [...cards, card] });
      else {
        const next = [...cards];
        next[idx] = card;
        set({ cards: next });
      }
    },

    removeCard: (id) => set({ cards: get().cards.filter((c) => c.id !== id) }),

    markFreshCard: (id) => {
      set({ freshCardId: id });
      setTimeout(() => {
        if (get().freshCardId === id) set({ freshCardId: null });
      }, 1500);
    },

    upsertLane: (lane) => {
      const { lanes } = get();
      const idx = lanes.findIndex((l) => l.id === lane.id);
      if (idx === -1) set({ lanes: [...lanes, lane].sort((a, b) => a.position - b.position) });
      else {
        const next = [...lanes];
        next[idx] = lane;
        set({ lanes: next.sort((a, b) => a.position - b.position) });
      }
    },

    removeLane: (id) => set({ lanes: get().lanes.filter((l) => l.id !== id) }),

    createLane: async (title, color) => {
      const boardId = get().activeBoardId ?? undefined;
      const lane = await api.createLane({ title, color, boardId });
      get().upsertLane(lane);
    },

    updateCard: async (id, patch) => {
      const updated = await api.updateCard(id, patch);
      get().upsertCard(updated);
      return updated;
    },

    deleteCard: async (id) => {
      await api.deleteCard(id);
      get().removeCard(id);
    },

    duplicateCard: async (id) => {
      const dup = await api.duplicateCard(id);
      get().upsertCard(dup);
      return dup;
    },

    createCategory: async (body) => {
      const cat = await api.createCategory(body);
      set({ categories: [...get().categories, cat].sort((a, b) => a.name.localeCompare(b.name)) });
      return cat;
    },

    updateCategoryItem: async (id, patch) => {
      const updated = await api.updateCategory(id, patch);
      const next = get().categories.map((c) => (c.id === id ? updated : c));
      set({ categories: next.sort((a, b) => a.name.localeCompare(b.name)) });
    },

    deleteCategoryItem: async (id) => {
      await api.deleteCategory(id);
      set({
        categories: get().categories.filter((c) => c.id !== id),
        cards: get().cards.map((c) => ({ ...c, categoryIds: c.categoryIds.filter((cid) => cid !== id) })),
      });
      if (get().categoryFilter === id) {
        set({ categoryFilter: null });
        syncFilters();
      }
    },

    loadDaily: async () => {
      set({ dailyLoading: true });
      try {
        const d = await api.proactiveDaily();
        set({ daily: d, dailyLoading: false });
      } catch {
        set({ dailyLoading: false });
      }
    },

    dismissDaily: () => {
      set({ dailyDismissed: true });
      try {
        localStorage.setItem(BANNER_DISMISSED_KEY, new Date().toISOString().slice(0, 10));
      } catch {}
    },

    loadSettings: async () => {
      try {
        const s = await api.getSettings();
        set({ userSettings: s });
      } catch {
        set({ userSettings: { hasGroqKey: false, groqLast4: null, nickname: null, tutorialCompleted: false, iagoBalloonDismissedAt: null, weightLaneIds: [] } });
      }
    },

    saveGroqKey: async (key) => {
      await api.putGroqKey(key);
      const s = await api.getSettings();
      set({ userSettings: s });
    },

    removeGroqKey: async () => {
      await api.deleteGroqKey();
      const s = await api.getSettings();
      set({ userSettings: s });
    },

    saveNickname: async (name) => {
      const s = await api.updateSettings({ nickname: name });
      set({ userSettings: s });
    },

    saveWeightLaneIds: async (ids) => {
      const s = await api.updateSettings({ weightLaneIds: ids });
      set({ userSettings: s });
    },

    markTutorialCompleted: async () => {
      const s = await api.updateSettings({ tutorialCompleted: true });
      set({ userSettings: s });
    },

    dismissIagoBalloon: async () => {
      const s = await api.updateSettings({ dismissIagoBalloon: true });
      set({ userSettings: s });
    },

    setOnboardingOpen: (v) => set({ onboardingOpen: v }),
    setUserEmail: (email) => set({ userEmail: email }),

    resetOnSignOut: () => {
      set({
        boards: [],
        activeBoardId: null,
        lanes: [],
        cards: [],
        categories: [],
        chatMessages: [],
        chatOpen: false,
        daily: null,
        userSettings: { hasGroqKey: false, groqLast4: null, nickname: null, tutorialCompleted: false, iagoBalloonDismissedAt: null, weightLaneIds: [] },
        onboardingOpen: false,
        userEmail: null,
        activeCardId: null,
        settingsOpen: false,
        focusLaneId: null,
        loading: true,
      });
      try {
        localStorage.removeItem(CHAT_OPEN_KEY);
        localStorage.removeItem(BANNER_DISMISSED_KEY);
        localStorage.removeItem(ACTIVE_BOARD_KEY);
      } catch {}
    },

    // ---- Chat ----
    toggleChat: () => {
      const next = !get().chatOpen;
      set({ chatOpen: next });
      try { localStorage.setItem(CHAT_OPEN_KEY, next ? "1" : "0"); } catch {}
      if (next && get().chatMessages.length === 0) {
        get().loadChat();
      }
    },

    loadChat: async () => {
      const msgs = await api.chatHistory().catch(() => []);
      set({ chatMessages: msgs });
    },

    sendChat: async (content) => {
      if (!content.trim() || get().chatSending) return;
      const optimisticUser: ChatMessage = {
        id: Date.now(),
        role: "user",
        content,
        actions: [],
        createdAt: new Date().toISOString(),
      };
      set({ chatSending: true, chatMessages: [...get().chatMessages, optimisticUser] });
      try {
        const { user, assistant } = await api.chatMessage(content, get().activeBoardId);
        const msgs = get().chatMessages.filter((m) => m.id !== optimisticUser.id);
        set({ chatMessages: [...msgs, user, assistant], chatSending: false });
      } catch (e) {
        const msgs = get().chatMessages.filter((m) => m.id !== optimisticUser.id);
        set({
          chatMessages: [
            ...msgs,
            optimisticUser,
            {
              id: Date.now() + 1,
              role: "assistant",
              content: `⚠️ Erro: ${(e as Error).message}`,
              actions: [],
              createdAt: new Date().toISOString(),
            },
          ],
          chatSending: false,
        });
      }
    },

    approveAction: async (messageId, index) => {
      const { message } = await api.chatExecute(messageId, index);
      const msgs = get().chatMessages.map((m) => (m.id === messageId ? message : m));
      set({ chatMessages: msgs });
      const active = get().activeBoardId;
      if (active != null) {
        await get().loadBoardContent(active);
      } else {
        await get().load();
      }
    },

    clearChat: async () => {
      await api.chatClear();
      set({ chatMessages: [] });
    },
  };
});
